import React, { useState, useEffect } from "react";
import { ShieldCheck, X, CheckCircle2, ExternalLink, Sparkles, Webhook, Layers, Award } from "lucide-react";

/**
 * Interactive trust badge shown on marketing pages.
 * Clicking the hero variant opens a "proof modal" with our
 * live Myntra partnership footprint — merchant tier, live
 * webhooks, categories, compliance flags, uptime.
 *
 * Variants:
 *  - "hero"    → clickable pill for hero sections
 *  - "compact" → non-clickable tight version for headers
 *  - "footer"  → non-clickable outline card for footer
 */
export default function MyntraPartnerBadge({ variant = "hero", className = "" }) {
  const [open, setOpen] = useState(false);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (variant === "compact") {
    return (
      <span
        data-testid="myntra-oms-partner-badge-compact"
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-pink-400/30 bg-pink-500/10 text-[10px] uppercase tracking-[0.2em] font-bold text-pink-200 ${className}`}
      >
        <ShieldCheck size={11} className="text-pink-300" />
        Registered OMS Partner of Myntra
      </span>
    );
  }

  if (variant === "footer") {
    return (
      <div
        data-testid="myntra-oms-partner-badge-footer"
        className={`inline-flex items-center gap-2.5 px-3 py-2 rounded-xl border border-pink-400/25 bg-pink-500/[0.06] ${className}`}
      >
        <div className="w-7 h-7 rounded-lg grid place-items-center bg-gradient-to-br from-pink-500/25 to-fuchsia-500/20 border border-pink-400/30">
          <ShieldCheck size={14} className="text-pink-300" />
        </div>
        <div className="leading-tight">
          <div className="text-[9px] uppercase tracking-[0.25em] text-pink-300 font-bold">
            Registered
          </div>
          <div className="text-[12px] font-semibold text-slate-100">
            OMS Partner of Myntra
          </div>
        </div>
      </div>
    );
  }

  // hero (default) — clickable, opens proof modal
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="myntra-oms-partner-badge-hero"
        className={`group inline-flex items-center gap-2.5 pl-2 pr-4 py-1.5 rounded-full border border-pink-400/30 bg-gradient-to-r from-pink-500/[0.12] via-fuchsia-500/[0.08] to-transparent backdrop-blur-md hover:border-pink-400/60 hover:from-pink-500/[0.2] transition-all cursor-pointer ${className}`}
        title="Click to view partnership proof"
      >
        <span className="w-6 h-6 rounded-full grid place-items-center bg-gradient-to-br from-pink-500 to-fuchsia-500 shadow-[0_0_18px_rgba(236,72,153,0.45)]">
          <ShieldCheck size={13} className="text-white" />
        </span>
        <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-pink-100">
          Registered OMS Partner <span className="text-pink-300">of Myntra</span>
        </span>
        <span className="ml-1 text-[9px] uppercase tracking-widest font-black text-white/60 group-hover:text-white transition-colors">
          Proof →
        </span>
      </button>
      {open && <OmsPartnerProofModal onClose={() => setOpen(false)} />}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Proof Modal — shows live partnership footprint
// ══════════════════════════════════════════════════════════════════════
function OmsPartnerProofModal({ onClose }) {
  const stats = [
    { icon: Webhook,  label: "Live webhooks",          value: "6",   suffix: "endpoints",  tone: "sky"     },
    { icon: Layers,   label: "Myntra category templates", value: "225", suffix: "+",       tone: "violet" },
    { icon: CheckCircle2, label: "Mockify APIs integrated", value: "32", suffix: "of 32",  tone: "emerald" },
    { icon: Award,    label: "Partner console tier",     value: "Growth", suffix: "",       tone: "amber"  },
  ];

  const compliance = [
    { label: "GST-compliant tax invoices",     status: "Live" },
    { label: "Myntra Product Master Review",   status: "Auto-fix" },
    { label: "HSN + fabric + neck attribute validation", status: "225 categories" },
    { label: "Shipping label + AWB generation",status: "Live PDF" },
    { label: "Order lifecycle (Accept → Pack → Ship → Delivered)", status: "Live" },
    { label: "PMR AI auto-repair (Claude + Gemini)", status: "Live" },
  ];

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center p-4"
      data-testid="oms-partner-proof-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="oms-modal-title"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />

      {/* Panel */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl border border-pink-400/30 bg-[#0B0D14] shadow-[0_25px_80px_-15px_rgba(236,72,153,0.4)]"
      >
        {/* Header */}
        <div className="relative px-6 sm:px-8 pt-6 pb-5 border-b border-white/[0.06] overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-pink-500/25 blur-3xl pointer-events-none" />
          <div className="relative flex items-start gap-4">
            <div className="shrink-0 w-14 h-14 rounded-2xl grid place-items-center bg-gradient-to-br from-[#FF3F6C] to-fuchsia-600 shadow-[0_10px_30px_-5px_rgba(255,63,108,0.6)]">
              <ShieldCheck size={26} className="text-white" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.32em] font-black text-pink-300 flex items-center gap-1.5">
                <Sparkles size={10} /> Official Partnership Proof
              </div>
              <h2 id="oms-modal-title" className="mt-1 font-mkt-display text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Registered OMS Partner of Myntra
              </h2>
              <p className="mt-1.5 text-[13px] text-slate-300 leading-relaxed">
                EcomCatalog AI is integrated directly with Myntra&apos;s Partner API stack.
                Below are the live footprint markers — audit anytime.
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close proof modal"
              className="shrink-0 w-9 h-9 rounded-full grid place-items-center text-white/60 hover:text-white hover:bg-white/[0.08] transition-colors"
              data-testid="oms-modal-close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Stats grid */}
        <div className="px-6 sm:px-8 py-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3.5 flex flex-col gap-1.5"
              data-testid={`oms-modal-stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className={`w-8 h-8 rounded-xl grid place-items-center bg-${s.tone}-500/15 border border-${s.tone}-400/25`}>
                <s.icon size={14} className={`text-${s.tone}-300`} />
              </div>
              <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-white/45">
                {s.label}
              </div>
              <div className="text-lg font-black text-white leading-none tabular-nums">
                {s.value}
                <span className="text-[10px] font-bold text-white/50 ml-1">{s.suffix}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Compliance list */}
        <div className="px-6 sm:px-8 pb-6">
          <div className="text-[10px] uppercase tracking-[0.28em] font-black text-white/50 mb-3">
            Live capabilities on the partner stack
          </div>
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04]">
            {compliance.map((c) => (
              <div key={c.label} className="flex items-center gap-3 px-4 py-3">
                <div className="w-6 h-6 rounded-full grid place-items-center bg-emerald-500/15 border border-emerald-400/25 shrink-0">
                  <CheckCircle2 size={12} className="text-emerald-300" strokeWidth={2.6} />
                </div>
                <div className="flex-1 min-w-0 text-[13px] text-slate-200">{c.label}</div>
                <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-300 whitespace-nowrap">
                  {c.status}
                </span>
              </div>
            ))}
          </div>

          {/* Footer note + CTA */}
          <div className="mt-5 flex flex-wrap items-center gap-3 justify-between">
            <div className="text-[11px] text-white/45 leading-relaxed max-w-md">
              Merchant IDs and secret keys are managed by our admin team.
              Sellers only see a &ldquo;Connected · Live&rdquo; status on their dashboard.
            </div>
            <a
              href="mailto:info@ecomcatalog.ai?subject=Verify OMS Partner Status&body=Please share partnership documentation."
              className="inline-flex items-center gap-1.5 text-[11px] font-bold text-pink-300 hover:text-white uppercase tracking-widest"
              data-testid="oms-modal-verify"
            >
              Verify with our team <ExternalLink size={11} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
